import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { useLanguage } from '../context/LanguageContext';
import { translations, stepsTranslations } from '../translations/content';
import Auth from './Auth';
import supabase from '../supabase/supabase';

const {
  FiArrowLeft, FiArrowRight, FiCheckCircle, FiTarget,
  FiLock, FiRocket, FiZap, FiSave, FiLoader,
  FiMessageCircle, FiPlus, FiTrash2, FiBookOpen,
  FiSmile, FiFrown, FiVideo, FiClipboard,
  FiEye, FiAward, FiGlobe, FiGrid
} = FiIcons;

const STEP_1_TABLE = 'step_1_vision_1773100000000';
const STEP_2_TABLE = 'step_2_knowledge_mapping_1775471543474';
const STEP_3_TABLE = 'step_3_ideas_1772902600000';
const STEP_4_TABLE = 'step_4_avatars_1775474655253';
const STEP_6_TABLE = 'step_6_relevance_1776000000000';
const STEP_7_TABLE = 'step_7_offers_1773460000000';
const STEP_8_TABLE = 'step_8_content_strategy_1776000000000';
const STEP_9_TABLE = 'step_9_mvp_features_1775481234567';
const WEBINAR_TABLE = 'webinar_registrations_1775485000000';

const StepDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { lang } = useLanguage();
  const t = translations[lang].detail;
  const p = translations[lang].paths;
  const steps = stepsTranslations[lang];
  const stepId = parseInt(id);
  const step = steps.find(s => s.id === stepId);

  const [user, setUser] = useState(null);
  const [hasAccess, setHasAccess] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  const [visionText, setVisionText] = useState('');
  const [knowledgeItems, setKnowledgeItems] = useState([]);
  const [step3Data, setStep3Data] = useState({ problem_solved: '', target_group: '', customer_needs: '', suitability: '', product_name_slogan: '' });
  const [avatars, setAvatars] = useState([]);
  const [step6Data, setStep6Data] = useState({ messaging: '', values: '' });
  const [step7Data, setStep7Data] = useState({ product_description: '', target_group: '', goal: '', shortcut: '', journey_before: '', journey_after: '' });
  const [step8Data, setStep8Data] = useState({ visibility: '', insignia: '', facts: '', networking: '' });
  const [step9Features, setStep9Features] = useState([]);
  const [webinarRegistered, setWebinarRegistered] = useState(false);

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      const u = session?.user ?? null;
      setUser(u);
      setHasAccess(!!u);
    });
  }, []);

  useEffect(() => { window.scrollTo(0, 0); }, [id]);

  useEffect(() => {
    if (!user) return;
    const load = async () => {
      try {
        if (stepId === 1) {
          const { data } = await supabase.from(STEP_1_TABLE).select('vision_text').eq('user_id', user.id).single();
          if (data) setVisionText(data.vision_text || '');
        } else if (stepId === 2) {
          const { data } = await supabase.from(STEP_2_TABLE).select('*').eq('user_id', user.id).order('sort_order', { ascending: true });
          if (data) setKnowledgeItems(data);
        } else if (stepId === 3) {
          const { data } = await supabase.from(STEP_3_TABLE).select('*').eq('user_id', user.id).single();
          if (data) setStep3Data(data);
        } else if (stepId === 4) {
          const { data } = await supabase.from(STEP_4_TABLE).select('*').eq('user_id', user.id).order('created_at', { ascending: true });
          if (data) setAvatars(data);
        } else if (stepId === 7) {
          const { data } = await supabase.from(STEP_7_TABLE).select('*').eq('user_id', user.id).single();
          if (data) setStep7Data(data);
        } else if (stepId === 9) {
          const { data } = await supabase.from(STEP_9_TABLE).select('*').eq('user_id', user.id).order('sort_order', { ascending: true });
          if (data) setStep9Features(data);
          const { data: wd } = await supabase.from(WEBINAR_TABLE).select('id').eq('user_id', user.id).single();
          if (wd) setWebinarRegistered(true);
        }
      } catch (e) { console.error(e); }
    };
    load();
  }, [stepId, user]);

  const doSave = async (table, data) => {
    if (!user) return;
    setSaving(true);
    const { error } = await supabase.from(table).upsert({ ...data, user_id: user.id, updated_at: new Date().toISOString() }, { onConflict: 'user_id' });
    if (!error) { setSaved(true); setTimeout(() => setSaved(false), 3000); }
    setSaving(false);
  };

  const addTopic = () => setKnowledgeItems([...knowledgeItems, { id: crypto.randomUUID(), topic: '', content: '', parent_id: null, sort_order: knowledgeItems.length }]);
  const addSubtopic = (pid) => setKnowledgeItems([...knowledgeItems, { id: crypto.randomUUID(), topic: '', content: '', parent_id: pid, sort_order: knowledgeItems.filter(i => i.parent_id === pid).length }]);
  const updateItem = (id, field, val) => setKnowledgeItems(knowledgeItems.map(i => i.id === id ? { ...i, [field]: val } : i));
  const deleteItem = (id) => setKnowledgeItems(knowledgeItems.filter(i => i.id !== id && i.parent_id !== id));

  const saveKnowledge = async () => {
    if (!user) return;
    setSaving(true);
    const items = knowledgeItems.map(({ id, ...rest }) => ({ ...rest, user_id: user.id }));
    await supabase.from(STEP_2_TABLE).delete().eq('user_id', user.id);
    const { error } = await supabase.from(STEP_2_TABLE).insert(items);
    if (!error) { setSaved(true); setTimeout(() => setSaved(false), 3000); }
    setSaving(false);
  };

  const SaveBtn = ({ onClick }) => (
    <button onClick={onClick} disabled={saving} className={`flex items-center space-x-2 px-10 py-5 rounded-full font-black shadow-lg transition-all active:scale-95 ${saved ? 'bg-green-500 text-white' : 'bg-blue-600 text-white hover:bg-blue-700'}`}>
      <SafeIcon icon={saving ? FiLoader : saved ? FiCheckCircle : FiSave} className={saving ? 'animate-spin' : ''} />
      <span>{saving ? (lang === 'de' ? 'Speichert...' : 'Saving...') : saved ? (lang === 'de' ? 'Gespeichert!' : 'Saved!') : (lang === 'de' ? 'Speichern' : 'Save')}</span>
    </button>
  );

  if (!step) return <div className="p-12 text-center text-gray-500">Step not found</div>;

  return (
    <div className="min-h-screen bg-gray-50 pb-20 pt-24">
      <div className="max-w-6xl mx-auto px-4">

        {/* Top nav */}
        <div className="flex items-center justify-between mb-8">
          <Link to={p.steps} className="group flex items-center space-x-2 text-gray-500 hover:text-blue-600 bg-white px-4 py-2 rounded-full shadow-sm border border-gray-100 transition-colors">
            <SafeIcon icon={FiArrowLeft} className="transition-transform group-hover:-translate-x-1" />
            <span className="text-sm font-bold">{t.back}</span>
          </Link>
          <span className="text-sm font-bold text-gray-400 bg-gray-100 px-3 py-1 rounded-full">{t.stepOf.replace('{current}', stepId)}</span>
        </div>

        {/* Banner */}
        <div className="bg-white rounded-[2.5rem] p-8 md:p-12 mb-8 shadow-sm border border-gray-100 relative overflow-hidden">
          <div className="absolute top-0 right-0 p-12 opacity-5 pointer-events-none font-black text-[200px] leading-none text-blue-900 select-none">{stepId}</div>
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="relative z-10">
            <div className="inline-flex items-center space-x-2 bg-blue-50 text-blue-700 px-3 py-1 rounded-full text-xs font-black uppercase tracking-widest mb-6">
              <SafeIcon icon={FiTarget} /><span>{t.overview}</span>
            </div>
            <h1 className="text-4xl md:text-6xl font-black text-gray-900 mb-4 tracking-tight leading-tight">{step.title}</h1>
            <div className="flex flex-col md:flex-row md:items-center gap-6 mt-8 p-6 bg-gray-50 rounded-3xl border border-gray-100 italic text-gray-600 font-medium">
              <div className="flex-shrink-0 w-12 h-12 bg-white rounded-2xl flex items-center justify-center shadow-sm">
                <SafeIcon icon={FiMessageCircle} className="text-blue-600 text-xl" />
              </div>
              <div>
                <p className="text-lg leading-relaxed">"{step.quote}"</p>
                <p className="text-sm font-black text-gray-400 mt-1">— {step.quoteAuthor}</p>
              </div>
            </div>
          </motion.div>
        </div>

        <div className="grid lg:grid-cols-3 gap-8">

          {/* ── Main column ── */}
          <div className="lg:col-span-2 space-y-8">

            {/* ── Overview card – always fully visible ── */}
            <div className="bg-white rounded-[2.5rem] p-8 md:p-10 shadow-sm border border-gray-100">
              <h2 className="text-2xl font-black text-gray-900 mb-6 flex items-center space-x-3">
                <div className="w-8 h-8 bg-blue-50 rounded-xl flex items-center justify-center">
                  <SafeIcon icon={FiBookOpen} className="text-blue-600" />
                </div>
                <span>{t.overview}</span>
              </h2>

              {/* description */}
              <p className="text-lg text-gray-700 leading-relaxed font-bold mb-6">{step.description}</p>

              {/* main content block */}
              <div className="p-6 bg-blue-50/60 rounded-3xl border border-blue-100 text-gray-700 text-base leading-relaxed mb-8 whitespace-pre-line">
                {step.content}
              </div>

              {/* action items */}
              {step.actionItems && step.actionItems.length > 0 && (
                <div>
                  <h3 className="text-xl font-black text-gray-900 mb-6 flex items-center space-x-3">
                    <div className="w-8 h-8 bg-green-50 rounded-xl flex items-center justify-center">
                      <SafeIcon icon={FiClipboard} className="text-green-600" />
                    </div>
                    <span>{t.actionItems}</span>
                  </h3>
                  <div className="grid gap-4">
                    {step.actionItems.map((item, i) => (
                      <div key={i} className="flex items-start space-x-4 p-5 bg-gray-50 rounded-2xl border border-gray-100 hover:bg-white hover:shadow-md transition-all">
                        <div className="w-8 h-8 flex-shrink-0 bg-white rounded-lg flex items-center justify-center border border-gray-100 text-blue-600 font-black text-xs shadow-sm">{i + 1}</div>
                        <span className="font-bold text-gray-700 pt-1">{item}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* ── Interactive tool – locked overlay when not logged in ── */}
            <div className="relative">
              {/* blurred placeholder when locked */}
              <div className={`space-y-8 transition-all duration-700 ${!hasAccess ? 'blur-xl grayscale opacity-20 pointer-events-none select-none' : ''}`}>

                {/* Step 1 */}
                {stepId === 1 && (
                  <div className="bg-white p-8 md:p-10 shadow-sm border border-gray-100 rounded-[2.5rem] space-y-6">
                    <h3 className="text-2xl font-black uppercase tracking-tight text-gray-900">{t.step1ToolTitle}</h3>
                    <p className="text-lg font-bold text-gray-700">{t.step1VisionQuestion}</p>
                    <p className="text-gray-500 italic font-medium">{t.step1VisionLeadIn}</p>
                    <textarea value={visionText} onChange={e => setVisionText(e.target.value)} rows={8} className="w-full p-6 bg-gray-50 rounded-3xl border-2 border-gray-100 focus:border-blue-500 focus:ring-0 transition-all text-gray-800 font-medium text-lg shadow-inner resize-none" placeholder={t.step1Placeholder} />
                    <SaveBtn onClick={() => doSave(STEP_1_TABLE, { vision_text: visionText })} />
                  </div>
                )}

                {/* Step 2 – Knowledge Mapping */}
                {stepId === 2 && (
                  <div className="bg-white p-8 md:p-10 shadow-sm border border-gray-100 rounded-[2.5rem] space-y-6">
                    <div className="flex items-center justify-between">
                      <h3 className="text-2xl font-black uppercase tracking-tight text-gray-900">{t.step2ToolTitle}</h3>
                      <button onClick={addTopic} className="flex items-center space-x-2 px-6 py-3 bg-blue-50 text-blue-600 rounded-full font-black text-sm hover:bg-blue-100 transition-all">
                        <SafeIcon icon={FiPlus} /><span>{t.step2AddTopic}</span>
                      </button>
                    </div>

                    {knowledgeItems.length === 0 && (
                      <p className="text-gray-400 italic text-center py-8">{t.step2Empty}</p>
                    )}

                    <div className="space-y-6">
                      {knowledgeItems.filter(i => !i.parent_id).map((topic, idx) => (
                        <div key={topic.id} className="p-6 bg-gray-50 rounded-[2rem] border border-gray-100 space-y-4">
                          <div className="flex items-center space-x-4">
                            <span className="w-10 h-10 bg-white rounded-xl flex items-center justify-center font-black text-blue-600 shadow-sm flex-shrink-0">{idx + 1}</span>
                            <input value={topic.topic} onChange={e => updateItem(topic.id, 'topic', e.target.value)} className="flex-1 bg-white px-4 py-3 rounded-xl border border-gray-200 focus:ring-2 focus:ring-blue-100 font-bold" placeholder={t.step2TopicPlaceholder} />
                            <button onClick={() => deleteItem(topic.id)} className="p-2 text-red-400 hover:text-red-600 transition-colors"><SafeIcon icon={FiTrash2} /></button>
                          </div>
                          <textarea value={topic.content} onChange={e => updateItem(topic.id, 'content', e.target.value)} rows={3} className="w-full p-4 bg-white rounded-xl border border-gray-200 focus:ring-2 focus:ring-blue-100 resize-none" placeholder={t.step2ContentPlaceholder} />
                          <div className="pl-8 space-y-3">
                            {knowledgeItems.filter(i => i.parent_id === topic.id).map(sub => (
                              <div key={sub.id} className="flex items-start space-x-3">
                                <div className="flex-1 bg-white border border-gray-200 rounded-xl p-3 space-y-2">
                                  <input value={sub.topic} onChange={e => updateItem(sub.id, 'topic', e.target.value)} className="w-full text-sm font-bold border-b border-gray-100 pb-1 focus:border-blue-400 outline-none bg-transparent" placeholder={t.step2TopicPlaceholder} />
                                  <textarea value={sub.content} onChange={e => updateItem(sub.id, 'content', e.target.value)} rows={2} className="w-full text-sm resize-none outline-none bg-transparent" placeholder={t.step2ContentPlaceholder} />
                                </div>
                                <button onClick={() => deleteItem(sub.id)} className="p-2 text-red-300 hover:text-red-500 mt-1"><SafeIcon icon={FiTrash2} /></button>
                              </div>
                            ))}
                            <button onClick={() => addSubtopic(topic.id)} className="flex items-center space-x-1 text-xs font-black text-blue-500 hover:text-blue-700">
                              <SafeIcon icon={FiPlus} /><span>{t.step2AddSubtopic}</span>
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                    <SaveBtn onClick={saveKnowledge} />
                  </div>
                )}

                {/* Step 3 */}
                {stepId === 3 && (
                  <div className="bg-white p-8 md:p-10 shadow-sm border border-gray-100 rounded-[2.5rem] space-y-8">
                    <h3 className="text-2xl font-black uppercase tracking-tight text-gray-900">{t.step3ToolTitle}</h3>
                    <div className="p-6 bg-blue-50 rounded-3xl border border-blue-100 text-gray-700 leading-relaxed">{t.step3ExplanatoryText}</div>
                    {[
                      { key: 'problem_solved', label: t.step3Question1 },
                      { key: 'target_group', label: t.step3Question2 },
                      { key: 'customer_needs', label: t.step3Question3 },
                      { key: 'suitability', label: t.step3Question4 },
                      { key: 'product_name_slogan', label: t.step3Question5 }
                    ].map(f => (
                      <div key={f.key} className="space-y-3">
                        <label className="block text-base font-black text-gray-900">{f.label}</label>
                        <textarea value={step3Data[f.key]} onChange={e => setStep3Data({ ...step3Data, [f.key]: e.target.value })} rows={4} className="w-full p-5 bg-gray-50 rounded-2xl border-2 border-gray-100 focus:border-blue-500 focus:ring-0 shadow-inner resize-none" placeholder={t.step3Placeholder} />
                      </div>
                    ))}
                    <SaveBtn onClick={() => doSave(STEP_3_TABLE, step3Data)} />
                  </div>
                )}

                {/* Step 4 */}
                {stepId === 4 && (
                  <div className="bg-white p-8 md:p-10 shadow-sm border border-gray-100 rounded-[2.5rem] space-y-8">
                    <div className="flex items-center justify-between">
                      <h3 className="text-2xl font-black uppercase tracking-tight text-gray-900">{t.step4ToolTitle}</h3>
                      <button onClick={async () => {
                        const { data } = await supabase.from(STEP_4_TABLE).insert({ name: 'New Avatar', demographics: '', motivations: '', challenges: '', user_id: user.id }).select().single();
                        if (data) setAvatars([...avatars, data]);
                      }} className="flex items-center space-x-2 px-5 py-3 bg-blue-50 text-blue-600 rounded-full font-black text-sm hover:bg-blue-100 transition-all">
                        <SafeIcon icon={FiPlus} /><span>{t.step4AddAvatar}</span>
                      </button>
                    </div>
                    <div className="p-6 bg-blue-50 rounded-3xl border border-blue-100 text-gray-700 leading-relaxed">{t.step4ExplanatoryText}</div>
                    <div className="grid md:grid-cols-2 gap-6">
                      {avatars.map(avatar => (
                        <div key={avatar.id} className="p-6 bg-gray-50 rounded-[2rem] border border-gray-100 space-y-4 relative group">
                          <button onClick={async () => { await supabase.from(STEP_4_TABLE).delete().eq('id', avatar.id); setAvatars(avatars.filter(a => a.id !== avatar.id)); }} className="absolute top-4 right-4 p-2 text-red-400 hover:text-red-600 opacity-0 group-hover:opacity-100 transition-all">
                            <SafeIcon icon={FiTrash2} />
                          </button>
                          <div>
                            <label className="block text-xs font-black uppercase tracking-widest text-gray-400 mb-1">{t.step4AvatarName}</label>
                            <input value={avatar.name} onChange={e => setAvatars(avatars.map(a => a.id === avatar.id ? { ...a, name: e.target.value } : a))} className="w-full bg-white px-3 py-2 rounded-xl border border-gray-200 font-bold" />
                          </div>
                          {[{ key: 'demographics', label: t.step4Demographics }, { key: 'motivations', label: t.step4Motivations }, { key: 'challenges', label: t.step4Challenges }].map(f => (
                            <div key={f.key}>
                              <label className="block text-xs font-black uppercase tracking-widest text-gray-400 mb-1">{f.label}</label>
                              <textarea value={avatar[f.key]} onChange={e => setAvatars(avatars.map(a => a.id === avatar.id ? { ...a, [f.key]: e.target.value } : a))} rows={3} className="w-full bg-white p-3 rounded-xl border border-gray-200 resize-none" />
                            </div>
                          ))}
                          <button onClick={() => doSave(STEP_4_TABLE, avatar)} className="w-full flex items-center justify-center space-x-2 py-3 bg-blue-600 text-white rounded-xl font-bold hover:bg-blue-700 transition-all">
                            <SafeIcon icon={FiSave} /><span>{t.step4Save}</span>
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Step 5 */}
                {stepId === 5 && (
                  <div className="bg-white p-8 md:p-12 shadow-sm border border-gray-100 rounded-[2.5rem] text-center space-y-8">
                    <div className="w-20 h-20 bg-blue-50 rounded-3xl flex items-center justify-center mx-auto"><SafeIcon icon={FiGrid} className="text-4xl text-blue-600" /></div>
                    <h3 className="text-3xl font-black text-gray-900">{t.step5ToolTitle}</h3>
                    <p className="text-xl text-gray-600 max-w-2xl mx-auto leading-relaxed">{t.step5Intro}</p>
                    <Link to={p.differentiation} className="inline-flex items-center space-x-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white px-10 py-5 rounded-full font-black text-lg shadow-xl hover:shadow-2xl transition-all transform hover:-translate-y-1">
                      <SafeIcon icon={FiArrowRight} /><span>{t.step5CTA}</span>
                    </Link>
                  </div>
                )}

                {/* Step 6 */}
                {stepId === 6 && (
                  <div className="bg-white p-8 md:p-10 shadow-sm border border-gray-100 rounded-[2.5rem] space-y-8">
                    <h3 className="text-2xl font-black uppercase tracking-tight text-gray-900">{t.step6ToolTitle}</h3>
                    <div className="p-6 bg-blue-50 rounded-3xl border border-blue-100 text-gray-700 leading-relaxed">{t.step6ExplanatoryText}</div>
                    <div className="space-y-6">
                      <div>
                        <label className="block text-base font-black text-gray-900 mb-3">{t.step6LabelMessaging}</label>
                        <textarea value={step6Data.messaging} onChange={e => setStep6Data({ ...step6Data, messaging: e.target.value })} rows={5} className="w-full p-5 bg-gray-50 rounded-2xl border-2 border-gray-100 focus:border-blue-500 focus:ring-0 shadow-inner resize-none" placeholder={t.step6PlaceholderMessaging} />
                      </div>
                      <div>
                        <label className="block text-base font-black text-gray-900 mb-3">{t.step6LabelValues}</label>
                        <textarea value={step6Data.values} onChange={e => setStep6Data({ ...step6Data, values: e.target.value })} rows={5} className="w-full p-5 bg-gray-50 rounded-2xl border-2 border-gray-100 focus:border-blue-500 focus:ring-0 shadow-inner resize-none" placeholder={t.step6PlaceholderValues} />
                      </div>
                    </div>
                    <SaveBtn onClick={() => doSave(STEP_6_TABLE, step6Data)} />
                  </div>
                )}

                {/* Step 7 */}
                {stepId === 7 && (
                  <div className="bg-white p-8 md:p-10 shadow-sm border border-gray-100 rounded-[2.5rem] space-y-8">
                    <h3 className="text-2xl font-black uppercase tracking-tight text-gray-900">{t.step7ToolTitle}</h3>
                    <div className="p-6 bg-blue-50 rounded-3xl border border-blue-100 text-gray-700 leading-relaxed">{t.step7ExplanatoryText}</div>
                    <div className="bg-gray-50 p-6 rounded-[2rem] border border-gray-100 space-y-6">
                      <h4 className="text-lg font-black text-blue-600 uppercase tracking-widest">{t.step7PerfectOfferTitle}</h4>
                      <div className="p-5 bg-white rounded-2xl border border-blue-100 font-black text-gray-900 shadow-sm">{t.step7PerfectOfferPhrase}</div>
                      <div className="grid md:grid-cols-2 gap-5">
                        {[
                          { key: 'product_description', label: t.step7LabelProduct, ph: t.step7PlaceholderProduct },
                          { key: 'target_group', label: t.step7LabelTarget, ph: t.step7PlaceholderTarget },
                          { key: 'goal', label: t.step7LabelGoal, ph: t.step7PlaceholderGoal },
                          { key: 'shortcut', label: t.step7LabelShortcut, ph: t.step7PlaceholderShortcut }
                        ].map(f => (
                          <div key={f.key}>
                            <label className="block text-xs font-black uppercase tracking-widest text-gray-400 mb-1">{f.label}</label>
                            <textarea value={step7Data[f.key]} onChange={e => setStep7Data({ ...step7Data, [f.key]: e.target.value })} rows={3} className="w-full p-3 bg-white rounded-xl border border-gray-200 focus:ring-2 focus:ring-blue-100 resize-none" placeholder={f.ph} />
                          </div>
                        ))}
                      </div>
                    </div>
                    <div>
                      <h4 className="text-xl font-black text-gray-900 mb-4">{t.step7JourneyTitle}</h4>
                      <div className="grid md:grid-cols-2 gap-6">
                        <div>
                          <label className="flex items-center space-x-2 text-red-500 font-bold mb-2"><SafeIcon icon={FiFrown} /><span>{t.step7JourneyBefore}</span></label>
                          <textarea value={step7Data.journey_before} onChange={e => setStep7Data({ ...step7Data, journey_before: e.target.value })} rows={5} className="w-full p-4 bg-gray-50 rounded-2xl border border-gray-200 focus:ring-2 focus:ring-red-100 resize-none" />
                        </div>
                        <div>
                          <label className="flex items-center space-x-2 text-green-500 font-bold mb-2"><SafeIcon icon={FiSmile} /><span>{t.step7JourneyAfter}</span></label>
                          <textarea value={step7Data.journey_after} onChange={e => setStep7Data({ ...step7Data, journey_after: e.target.value })} rows={5} className="w-full p-4 bg-gray-50 rounded-2xl border border-gray-200 focus:ring-2 focus:ring-green-100 resize-none" />
                        </div>
                      </div>
                    </div>
                    <SaveBtn onClick={() => doSave(STEP_7_TABLE, step7Data)} />
                  </div>
                )}

                {/* Step 8 */}
                {stepId === 8 && (
                  <div className="bg-white p-8 md:p-10 shadow-sm border border-gray-100 rounded-[2.5rem] space-y-8">
                    <h3 className="text-2xl font-black uppercase tracking-tight text-gray-900">{t.step8ToolTitle}</h3>
                    <div className="p-6 bg-blue-50 rounded-3xl border border-blue-100 text-gray-700 leading-relaxed">{t.step8Intro}</div>
                    <div className="grid md:grid-cols-2 gap-6">
                      {[
                        { key: 'visibility', label: t.step8LabelVisibility, icon: FiEye },
                        { key: 'insignia', label: t.step8LabelInsignia, icon: FiAward },
                        { key: 'facts', label: t.step8LabelFacts, icon: FiBookOpen },
                        { key: 'networking', label: t.step8LabelNetworking, icon: FiGlobe }
                      ].map(f => (
                        <div key={f.key} className="space-y-2">
                          <label className="flex items-center space-x-2 font-black text-gray-900">
                            <SafeIcon icon={f.icon} className="text-blue-600" /><span>{f.label}</span>
                          </label>
                          <textarea value={step8Data[f.key]} onChange={e => setStep8Data({ ...step8Data, [f.key]: e.target.value })} rows={4} className="w-full p-4 bg-gray-50 rounded-2xl border border-gray-100 focus:ring-2 focus:ring-blue-100 shadow-inner resize-none" placeholder={t.step8Placeholder} />
                        </div>
                      ))}
                    </div>
                    <SaveBtn onClick={() => doSave(STEP_8_TABLE, step8Data)} />
                  </div>
                )}

                {/* Step 9 */}
                {stepId === 9 && (
                  <div className="bg-white p-8 md:p-10 shadow-sm border border-gray-100 rounded-[2.5rem] space-y-8">
                    <h3 className="text-2xl font-black uppercase tracking-tight text-gray-900">{t.step9ToolTitle}</h3>
                    <div className="p-6 bg-blue-50 rounded-3xl border border-blue-100 text-gray-700 leading-relaxed">{t.step9ExplanatoryText}</div>
                    <div className="bg-gradient-to-r from-blue-600 to-purple-600 p-6 rounded-[2rem] text-white flex flex-col md:flex-row items-center justify-between gap-6 shadow-xl">
                      <div className="space-y-1">
                        <div className="inline-block px-3 py-1 bg-white/20 rounded-full text-xs font-black uppercase tracking-widest">{t.step9WebinarTitle}</div>
                        <p className="font-bold opacity-90">{t.step9WebinarDesc}</p>
                      </div>
                      <button onClick={async () => { await supabase.from(WEBINAR_TABLE).upsert({ user_id: user.id }); setWebinarRegistered(true); }} disabled={webinarRegistered} className={`flex items-center space-x-2 px-7 py-4 rounded-full font-black shadow-lg whitespace-nowrap transition-all active:scale-95 ${webinarRegistered ? 'bg-green-400 text-white cursor-default' : 'bg-white text-blue-600 hover:bg-blue-50'}`}>
                        <SafeIcon icon={webinarRegistered ? FiCheckCircle : FiVideo} />
                        <span>{webinarRegistered ? t.step9WebinarSuccess : t.step9WebinarCTA}</span>
                      </button>
                    </div>
                    <div className="space-y-4">
                      <div className="flex items-center justify-between">
                        <h4 className="text-lg font-black text-gray-900 uppercase tracking-widest">MVP Feature Roadmap</h4>
                        <button onClick={() => setStep9Features([...step9Features, { id: crypto.randomUUID(), feature_name: '', priority: 'Medium', status: 'Planned', sort_order: step9Features.length }])} className="flex items-center space-x-2 px-5 py-3 bg-blue-50 text-blue-600 rounded-full font-black text-sm hover:bg-blue-100">
                          <SafeIcon icon={FiPlus} /><span>{t.step9AddFeature}</span>
                        </button>
                      </div>
                      {step9Features.map(feat => (
                        <div key={feat.id} className="grid grid-cols-1 md:grid-cols-4 gap-3 p-4 bg-gray-50 rounded-2xl border border-gray-100 items-center">
                          <input value={feat.feature_name} onChange={e => setStep9Features(step9Features.map(f => f.id === feat.id ? { ...f, feature_name: e.target.value } : f))} className="md:col-span-2 bg-white px-4 py-2 rounded-xl border border-gray-200 text-sm font-bold" placeholder={t.step9FeatureName} />
                          <select value={feat.priority} onChange={e => setStep9Features(step9Features.map(f => f.id === feat.id ? { ...f, priority: e.target.value } : f))} className="bg-white px-3 py-2 rounded-xl border border-gray-200 text-sm font-bold">
                            <option value="High">High</option><option value="Medium">Medium</option><option value="Low">Low</option>
                          </select>
                          <div className="flex items-center space-x-2">
                            <select value={feat.status} onChange={e => setStep9Features(step9Features.map(f => f.id === feat.id ? { ...f, status: e.target.value } : f))} className="flex-1 bg-white px-3 py-2 rounded-xl border border-gray-200 text-sm font-bold">
                              <option value="Planned">Planned</option><option value="In Progress">In Progress</option><option value="Done">Done</option>
                            </select>
                            <button onClick={() => setStep9Features(step9Features.filter(f => f.id !== feat.id))} className="p-2 text-red-400 hover:text-red-600"><SafeIcon icon={FiTrash2} /></button>
                          </div>
                        </div>
                      ))}
                      <SaveBtn onClick={async () => {
                        setSaving(true);
                        const items = step9Features.map(({ id, ...r }) => ({ ...r, user_id: user.id }));
                        await supabase.from(STEP_9_TABLE).delete().eq('user_id', user.id);
                        await supabase.from(STEP_9_TABLE).insert(items);
                        setSaved(true); setTimeout(() => setSaved(false), 3000);
                        setSaving(false);
                      }} />
                    </div>
                  </div>
                )}

                {/* Step 10 */}
                {stepId === 10 && (
                  <div className="bg-white p-8 md:p-12 shadow-sm border border-gray-100 rounded-[2.5rem] text-center space-y-8">
                    <div className="w-20 h-20 bg-green-50 rounded-3xl flex items-center justify-center mx-auto"><SafeIcon icon={FiRocket} className="text-4xl text-green-600" /></div>
                    <h3 className="text-3xl font-black text-gray-900">Launch Your Vision</h3>
                    <p className="text-xl text-gray-600 max-w-2xl mx-auto leading-relaxed">You've built the foundation. Now it's time to execute your go-to-market strategy and share your solution with the world.</p>
                    <div className="flex flex-col md:flex-row items-center justify-center gap-4">
                      <button className="flex items-center space-x-3 bg-blue-600 text-white px-8 py-4 rounded-full font-black hover:bg-blue-700 transition-all shadow-lg active:scale-95"><SafeIcon icon={FiZap} /><span>Download Launch Checklist</span></button>
                      <button className="flex items-center space-x-3 bg-white text-gray-900 px-8 py-4 rounded-full font-black border border-gray-200 hover:bg-gray-50 transition-all shadow-sm active:scale-95"><SafeIcon icon={FiAward} /><span>Claim Completion Badge</span></button>
                    </div>
                  </div>
                )}

              </div>

              {/* Locked overlay */}
              {!hasAccess && (
                <div className="absolute inset-0 z-20 flex items-center justify-center p-6 bg-white/60 backdrop-blur-sm rounded-[2.5rem]">
                  <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} className="bg-white p-8 md:p-10 rounded-[2.5rem] shadow-2xl border border-blue-100 max-w-lg w-full">
                    <div className="text-center mb-8">
                      <div className="w-16 h-16 bg-blue-50 rounded-2xl flex items-center justify-center mx-auto mb-6">
                        <SafeIcon icon={FiLock} className="text-3xl text-blue-600" />
                      </div>
                      <h4 className="text-2xl font-black text-gray-900 mb-3 uppercase tracking-tight">{t.lockedTitle}</h4>
                      <p className="text-gray-600 font-medium">{t.authPrompt}</p>
                    </div>
                    <div className="bg-gray-50 p-6 rounded-[2rem] border border-gray-100">
                      <Auth embedded={true} initialIsLogin={false} />
                    </div>
                  </motion.div>
                </div>
              )}
            </div>
          </div>

          {/* ── Sidebar ── */}
          <div className="space-y-8">

            {/* Step navigation */}
            <div className="bg-white rounded-[2.5rem] p-6 shadow-sm border border-gray-100">
              <div className="flex items-center space-x-3 mb-5">
                <div className="w-8 h-8 bg-blue-50 rounded-xl flex items-center justify-center">
                  <SafeIcon icon={FiArrowRight} className="text-blue-600" />
                </div>
                <h3 className="font-black uppercase tracking-widest text-sm text-gray-900">
                  {lang === 'de' ? 'Schritte navigieren' : 'Navigate Steps'}
                </h3>
              </div>
              <div className={`grid gap-3 ${stepId > 1 && stepId < 10 ? 'grid-cols-2' : 'grid-cols-1'}`}>
                {stepId > 1 && (
                  <button onClick={() => navigate(`${p.steps}/${stepId - 1}`)} className="flex items-center justify-center space-x-2 p-4 bg-gray-50 hover:bg-blue-50 hover:text-blue-600 rounded-2xl border border-gray-100 transition-all group">
                    <SafeIcon icon={FiArrowLeft} className="transition-transform group-hover:-translate-x-1" />
                    <span className="font-black text-xs uppercase tracking-widest">{t.prevStep}</span>
                  </button>
                )}
                {stepId < 10 && (
                  <button onClick={() => navigate(`${p.steps}/${stepId + 1}`)} className="flex items-center justify-center space-x-2 p-4 bg-gray-50 hover:bg-blue-50 hover:text-blue-600 rounded-2xl border border-gray-100 transition-all group">
                    <span className="font-black text-xs uppercase tracking-widest">{t.nextStep}</span>
                    <SafeIcon icon={FiArrowRight} className="transition-transform group-hover:translate-x-1" />
                  </button>
                )}
              </div>

              {/* All steps mini-list */}
              <div className="mt-5 space-y-1 border-t border-gray-100 pt-5">
                {steps.map(s => (
                  <button key={s.id} onClick={() => navigate(`${p.steps}/${s.id}`)} className={`w-full flex items-center space-x-3 px-3 py-2 rounded-xl text-left transition-all text-xs font-black uppercase tracking-widest ${s.id === stepId ? 'bg-blue-600 text-white' : 'text-gray-500 hover:bg-gray-50 hover:text-blue-600'}`}>
                    <span className={`w-5 h-5 flex-shrink-0 rounded-md flex items-center justify-center text-[10px] font-black ${s.id === stepId ? 'bg-white/20' : 'bg-gray-100'}`}>{s.id}</span>
                    <span className="truncate">{s.title}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Key focus points */}
            <div className="bg-gray-900 rounded-[2.5rem] p-8 text-white shadow-xl sticky top-24">
              <div className="flex items-center space-x-3 mb-6">
                <SafeIcon icon={FiTarget} className="text-blue-400 text-xl" />
                <h3 className="font-black uppercase tracking-widest text-sm">{t.keyFocus}</h3>
              </div>
              <ul className="space-y-3">
                {step.keyPoints?.map((point, i) => (
                  <li key={i} className="flex items-center space-x-3 p-3 rounded-2xl bg-white/5 border border-white/5 hover:bg-white/10 transition-all uppercase text-xs font-black tracking-widest leading-relaxed">
                    <SafeIcon icon={FiZap} className="text-blue-400 flex-shrink-0" />
                    <span>{point}</span>
                  </li>
                ))}
              </ul>
            </div>

          </div>
        </div>
      </div>
    </div>
  );
};

export default StepDetail;